import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { MapPin } from 'lucide-react'
import { Card, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { LogoUploadField } from '@/shared/components/logo-upload'
import { AddressFieldsEditor } from '@/shared/components/address-fields'
import type { DirtyMap } from '@/shared/utils/diffAddressesForUpdate'
import {
  updateOrganizationSchema,
  type UpdateOrganizationFormValues,
} from '../../schemas/updateOrganizationSchema'
import type { MyOrganization } from '../../hooks/useMyOrganization'

export const ORGANIZATION_EDIT_FORM_ID = 'org-edit-form'

interface OrganizationEditFormProps {
  organization: NonNullable<MyOrganization>
  onSubmit: (
    values: UpdateOrganizationFormValues,
    dirty: DirtyMap<UpdateOrganizationFormValues>,
  ) => void | Promise<void>
  disabled?: boolean
}

function deriveDefaults(org: NonNullable<MyOrganization>): UpdateOrganizationFormValues {
  const license = org.licenseType === 'ANNUAL' ? 'ANNUAL' : 'MONTHLY'
  return {
    legalName: org.legalName ?? '',
    // Se conserva para no romper el schema, pero ya no se edita (es quien creó la org).
    propietaryName: org.propietaryName ?? '',
    description: org.description ?? '',
    logo: org.logo ?? '',
    // Datos de facturación: no editables por el usuario; se mantienen como están.
    licenseType: license,
    plannedDayToPay: org.plannedDayToPay ?? null,
    addresses: (org.addresses ?? []).map((a) => ({
      id: a.id,
      _deleted: false,
      street1: a.street1 ?? '',
      city: a.city ?? '',
      countryId: a.countryId ?? 0,
      provinceId: a.provinceId ?? 0,
      municipalityId: a.municipalityId ?? 0,
      isMain: a.isMain ?? false,
    })),
  }
}

/** Mapa con la ubicación aproximada de la dirección principal de la organización. */
function AddressLocationMap() {
  const addresses = useWatch({ name: 'addresses' }) as
    | { street1?: string; city?: string; isMain?: boolean; _deleted?: boolean }[]
    | undefined
  const active = (addresses ?? []).filter((a) => !a._deleted)
  const main = active.find((a) => a.isMain) ?? active[0]
  const query = [main?.street1, main?.city].filter(Boolean).join(', ').trim()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ubicación en el mapa</CardTitle>
      </CardHeader>
      {query ? (
        <div>
          <div className="overflow-hidden rounded-xl border border-line">
            <iframe
              title="Ubicación del negocio"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(`${query}, Cuba`)}&z=14&output=embed`}
              className="h-64 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-ink-muted">
            <MapPin className="h-3.5 w-3.5" />
            Ubicación aproximada según la calle y ciudad de la dirección principal.
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line bg-surface py-10 text-center">
          <MapPin className="h-6 w-6 text-ink-subtle" />
          <p className="mt-2 text-sm text-ink-muted">
            Completa la calle y la ciudad para ver la ubicación en el mapa.
          </p>
        </div>
      )}
    </Card>
  )
}

/**
 * Form de edición de organización. NO renderiza sus propios botones de submit
 * — el page padre los coloca en su header y los enlaza con `form={ORGANIZATION_EDIT_FORM_ID}`.
 */
export function OrganizationEditForm({
  organization,
  onSubmit,
  disabled,
}: OrganizationEditFormProps) {
  const form = useForm<UpdateOrganizationFormValues>({
    resolver: zodResolver(updateOrganizationSchema),
    defaultValues: deriveDefaults(organization),
    mode: 'onBlur',
  })

  const submit = form.handleSubmit(async (values) => {
    await onSubmit(values, form.formState.dirtyFields)
  })

  return (
    <FormProvider {...form}>
      <form id={ORGANIZATION_EDIT_FORM_ID} onSubmit={submit} className="space-y-6">
        <fieldset disabled={disabled} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Datos generales</CardTitle>
            </CardHeader>
            <div className="grid gap-6 sm:grid-cols-[140px_1fr]">
              <FormField
                control={form.control}
                name="logo"
                render={({ field }) => (
                  <FormItem className="flex items-start justify-center sm:justify-start">
                    <LogoUploadField
                      value={field.value ?? null}
                      onChange={(url) => field.onChange(url ?? '')}
                      disabled={disabled}
                    />
                  </FormItem>
                )}
              />

              <div className="grid gap-5">
                <FormField
                  control={form.control}
                  name="legalName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Nombre legal</FormLabel>
                      <FormControl>
                        <Input required {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descripción</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormDescription>Opcional. Hasta 500 caracteres.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </Card>

          <AddressFieldsEditor name="addresses" title="Direcciones" />

          <AddressLocationMap />
        </fieldset>
      </form>
    </FormProvider>
  )
}
